import { type FormEvent, useState } from 'react';
import { useTranslation } from '@doccareplus/i18n';
import { Button, DateField, Drawer, FieldLabelMark, SearchSelect, Textarea, TimeField } from '@doccareplus/ui';
import { useTimeFormat } from '@doccareplus/kernel';
import { showToast, toastApiError } from '@doccareplus/kernel/application';
import { cn } from '@doccareplus/shared/lib/utils';
import {
  useBookAppointmentCommand,
  useCreateWalkInCommand,
} from '../application/scheduling.command';
import { useBracketLoadQuery, useProvidersQuery, useStaffBookingOptionsQuery } from '../application/scheduling.query';
import {
  bookingStartTimeIso,
  currentTimeInput,
  findServerApprovedStartTime,
  isBookingInPast,
  maxBookableDateInput,
  minSelectableTimeInput,
  todayDateInput,
} from '../domain/appointmentView';
import type { PatientPickOption } from '../infrastructure/scheduling.api';
import { PatientSearchAndLink, type QuickRegisterPrefillFromSearch } from './PatientSearchAndLink';
import { QuickRegisterPatientStep } from './QuickRegisterPatientStep';
import { BookingStepIndicator } from './BookingStepIndicator';
import { useQuickRegisterPatientStep } from './useQuickRegisterPatientStep';
import { AvailableSlotPicker } from './AvailableSlotPicker';
import { AppointmentDurationField } from './AppointmentDurationField';

const FORM_ID = 'book-appointment-form';
const REGISTER_FORM_ID = 'book-appointment-register-form';

/** The drawer opens in one of two flows: a future scheduled booking, or an immediate walk-in. */
export type BookingMode = 'scheduled' | 'walkin';

/**
 * Book a scheduled staff appointment (confirmed immediately) or register a walk-in from a right-side drawer
 * — the design's `sheetIn` sheet, not a centered dialog. A segmented control switches between the two flows:
 * scheduled bookings pick a future date/time; walk-ins join today's queue now.
 *
 * Patient discovery is handled by `PatientSearchAndLink`: staff either select an existing patient, or hit
 * "Register new patient" which enters a 2-step in-drawer stepper (register → booking details) — Step 1
 * captures full patient details and creates the patient immediately (it exists whether or not staff go on
 * to complete Step 2), then auto-advances back to booking details with the new patient linked. Provider is
 * an optional picker backed by the clinic staff list.
 */
export function BookAppointmentDrawer({
  isOpen,
  onClose,
  defaultDate,
  defaultTime,
  defaultMode = 'scheduled',
  defaultPatient = null,
}: {
  isOpen: boolean;
  onClose: () => void;
  defaultDate: string;
  /** Pre-fills the time field (e.g. the exact hour clicked in the desktop time-grid). Falls back to now. */
  defaultTime?: string;
  defaultMode?: BookingMode;
  /** Pre-selects the patient (e.g. opening the drawer from a patient's chart). Still editable via search. */
  defaultPatient?: PatientPickOption | null;
}) {
  const { t } = useTranslation('appointments');
  const { timeFormat } = useTimeFormat();
  const book = useBookAppointmentCommand();
  const walkIn = useCreateWalkInCommand();
  const { data: providers } = useProvidersQuery();
  const registerForm = useQuickRegisterPatientStep();

  const [mode, setMode] = useState<BookingMode>(defaultMode);
  const [patient, setPatient] = useState<PatientPickOption | null>(defaultPatient);
  const [providerId, setProviderId] = useState('');
  const [date, setDate] = useState(defaultDate);
  // Default to the current wall-clock time (rounded up to the next 5 min) rather than a fixed 09:00,
  // so the pre-filled slot matches "book this in now-ish" — the common front-desk case. A caller that
  // knows the exact slot clicked (the time-grid) overrides this via `defaultTime`.
  const [time, setTime] = useState(() => defaultTime ?? currentTimeInput());
  const [pastError, setPastError] = useState(false);
  const [dateRequiredError, setDateRequiredError] = useState(false);
  const [patientRequiredError, setPatientRequiredError] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(0);
  const [reason, setReason] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [strictCustomTimeSelected, setStrictCustomTimeSelected] = useState(false);
  const [customTimeDialogOpen, setCustomTimeDialogOpen] = useState(false);

  // The register-patient stepper: entering it hides the booking fields and shows Step 1 (full patient
  // details) instead. `registeredViaStepper` tracks whether the currently linked patient came from this
  // flow, so Step 2 can offer a read-only "view registration" link back to what was submitted.
  const [flowStep, setFlowStep] = useState<'booking' | 'register'>('booking');
  const [registeredViaStepper, setRegisteredViaStepper] = useState(false);

  // Advisory appointment-overload check (see .ai/BusinessDomain/scheduling-queue.md, "Appointment
  // Overload"): scheduled bookings only, and only once a concrete provider is chosen — "any doctor" and
  // walk-ins are never checked in v1. Warn-not-block: an inline banner plus a relabeled submit button is
  // the confirmation step — there is no hard stop, staff can always proceed.
  const isWalkIn = mode === 'walkin';
  const bookingOptions = useStaffBookingOptionsQuery(date, providerId, durationMinutes, !isWalkIn);
  const allowFreeFormScheduling = bookingOptions.data?.allowFreeFormScheduling ?? false;
  const startTimeIso = !isWalkIn && date && time ? bookingStartTimeIso(date, time) : '';
  const bracketLoad = useBracketLoadQuery(
    providerId,
    startTimeIso,
    !isWalkIn && allowFreeFormScheduling && Boolean(providerId),
  );
  const isOverloaded = Boolean(bracketLoad.data?.isOverloaded);

  // Reopening a still-mounted drawer must start clean. `useState` initializers only run on
  // first mount, so at persistent mount sites (SchedulePage/Queue keep it mounted and toggle
  // `isOpen`) a completed booking — or an abandoned edit — would otherwise leak stale fields
  // into the next open. Reset on the open transition using React's render-phase prop-change
  // pattern (not an effect, so no extra render round-trip).
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setMode(defaultMode);
      setPatient(defaultPatient);
      setProviderId('');
      setDate(defaultDate);
      setTime(defaultTime ?? currentTimeInput());
      setPastError(false);
      setDateRequiredError(false);
      setPatientRequiredError(false);
      setDurationMinutes(0);
      setReason('');
      setInternalNote('');
      setSelectedSlot('');
      setStrictCustomTimeSelected(false);
      setCustomTimeDialogOpen(false);
      setFlowStep('booking');
      setRegisteredViaStepper(false);
    }
  }

  // Single-provider clinics: pre-select the only doctor and lock the field — there's nothing to choose. Adjusted
  // during render (like the reset above) rather than in an effect, so no render ever shows the field unselected.
  const soleProvider = providers?.length === 1 ? providers[0] : null;
  if (soleProvider && !providerId) {
    setProviderId(soleProvider.id);
  }

  if (!isOpen) return null;

  const enterRegisterStep = (prefill: QuickRegisterPrefillFromSearch) => {
    registerForm.resetFields({ name: prefill.name, phone: prefill.phone, phoneCountry: prefill.phoneCountry });
    setFlowStep('register');
  };

  const handleRegisterSubmit = async () => {
    const created = await registerForm.submit();
    if (created) {
      setPatient(created);
      setPatientRequiredError(false);
      setRegisteredViaStepper(true);
      setFlowStep('booking');
    }
  };

  // ── Step 1: register patient ────────────────────────────────────────
  if (flowStep === 'register') {
    // Re-entering after the patient already exists (the "view registration" link) is a read-only
    // summary, never a re-submit — the patient was created the moment Step 1 first succeeded.
    const readOnly = registeredViaStepper && Boolean(patient);
    return (
      <Drawer
        title={t('booking.quickRegister.stepper.register', { defaultValue: 'Register patient' })}
        description={t('booking.quickRegister.stepOf', { current: 1, total: 2 })}
        closeLabel={t('common:actions.close', { defaultValue: 'Close' })}
        onClose={onClose}
        dismissible={false}
        footer={
          readOnly ? (
            <>
              <Button type="button" variant="ghost" fullWidth onClick={onClose}>
                {t('common:actions.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button type="button" variant="primary" fullWidth onClick={() => setFlowStep('booking')}>
                {t('common:actions.continue', { defaultValue: 'Continue' })}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" fullWidth onClick={onClose} disabled={registerForm.isSubmitting}>
                {t('common:actions.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button type="button" variant="ghost" fullWidth onClick={() => setFlowStep('booking')} disabled={registerForm.isSubmitting}>
                {t('common:actions.back', { defaultValue: 'Back' })}
              </Button>
              <Button type="submit" form={REGISTER_FORM_ID} variant="primary" fullWidth isLoading={registerForm.isSubmitting}>
                {registerForm.duplicateMatches.length ? t('booking.quickRegister.continue') : t('booking.quickRegister.submit')}
              </Button>
            </>
          )
        }
      >
        <div className="flex flex-col gap-4">
          <BookingStepIndicator step="register" />
          <QuickRegisterPatientStep
            formId={REGISTER_FORM_ID}
            form={registerForm}
            onSubmit={() => void handleRegisterSubmit()}
            onSelectExisting={(match) => {
              setPatient(match);
              setPatientRequiredError(false);
              setRegisteredViaStepper(false);
              setFlowStep('booking');
            }}
            readOnly={readOnly}
          />
        </div>
      </Drawer>
    );
  }

  const pending = isWalkIn ? walkIn.isPending : book.isPending;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!patient) {
      setPatientRequiredError(true);
      return;
    }
    if (durationMinutes === 0) return;
    if (isWalkIn) {
      walkIn.mutate(
        {
          request: {
            durationMinutes,
            patientId: patient?.id ?? null,
            providerId: providerId || null,
            reason: reason || null,
          },
          patientName: patient?.fullName,
        },
        {
          onSuccess: () => {
            showToast({ type: 'success', message: t('booking.toasts.walkInCreated') });
            onClose();
          },
          onError: (error) => toastApiError(error, t('common:errors.generic')),
        }
      );
      return;
    }
    if (!date) {
      setDateRequiredError(true);
      return;
    }
    if (!allowFreeFormScheduling && (!providerId || !selectedSlot)) {
      return;
    }
    // Mirror the server's not-in-past guard so staff get immediate feedback (staff book on-site,
    // so browser-local ≈ clinic-local). The server stays authoritative for the clinic-local check.
    if (allowFreeFormScheduling && isBookingInPast(date, time)) {
      setPastError(true);
      return;
    }
    book.mutate(
      {
        request: {
          startTime: allowFreeFormScheduling ? (selectedSlot || bookingStartTimeIso(date, time)) : selectedSlot,
          durationMinutes,
          patientId: patient?.id ?? null,
          providerId: providerId || null,
          reason: reason || null,
          internalNote: internalNote || null,
        },
        patientName: patient?.fullName,
      },
      {
        onSuccess: () => {
          showToast({ type: 'success', message: t('booking.toasts.appointmentCreated') });
          onClose();
        },
        onError: (error) => toastApiError(error, t('common:errors.generic')),
      }
    );
  };

  const doctorPicker = (
    <SearchSelect
      label={t('booking.doctorLabel')}
      optional={isWalkIn || allowFreeFormScheduling}
      requiredMark={!isWalkIn && !allowFreeFormScheduling}
      value={providerId}
      onChange={(value) => {
        setProviderId(value);
        setSelectedSlot('');
        setStrictCustomTimeSelected(false);
        setCustomTimeDialogOpen(false);
      }}
      disabled={Boolean(soleProvider)}
      placeholder={t('booking.anyDoctor')}
      searchPlaceholder={t('common:actions.search', { defaultValue: 'Search' })}
      noResultsLabel={t('common:list.noResults', { defaultValue: 'No results' })}
      options={[
        ...(isWalkIn || allowFreeFormScheduling ? [{ value: '', label: t('booking.anyDoctor') }] : []),
        ...(providers?.map((provider) => ({ value: provider.id, label: provider.name })) ?? []),
      ]}
    />
  );

  const durationField = (
    <AppointmentDurationField
      valueMinutes={durationMinutes}
      onChange={(value) => {
        setDurationMinutes(value);
        setSelectedSlot('');
        setStrictCustomTimeSelected(false);
        setCustomTimeDialogOpen(false);
      }}
    />
  );

  return (
    <Drawer
      title={isWalkIn ? t('booking.walkInTitle') : t('booking.title')}
      closeLabel={t('common:actions.close', { defaultValue: 'Close' })}
      onClose={onClose}
      dismissible={false}
      footer={
        <>
          <Button type="button" variant="ghost" fullWidth onClick={onClose}>
            {t('common:actions.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            variant="primary"
            fullWidth
            isLoading={pending}
            disabled={durationMinutes === 0 || (isWalkIn
              ? !patient
              : Boolean(date) && !allowFreeFormScheduling && (!providerId || !selectedSlot))}
          >
            {isWalkIn
              ? t('booking.walkInSubmit')
              : isOverloaded
                ? t('booking.overload.confirmSubmit', { defaultValue: 'Confirm & book anyway' })
                : t('booking.submit')}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit} className="flex flex-col gap-4">
        {registeredViaStepper && <BookingStepIndicator step="details" />}

        {/* Mode toggle — Scheduled vs Walk-in (design-token segmented control). */}
        <div className="flex overflow-hidden rounded-[9px] border border-border">
          {(['scheduled', 'walkin'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setMode(option)}
              className={cn(
                'flex-1 px-3.5 py-2 text-[12.5px] font-semibold transition-colors',
                mode === option ? 'bg-primary-weak text-primary' : 'bg-surface text-text-2 hover:bg-surface-2'
              )}
            >
              {t(option === 'scheduled' ? 'booking.modeScheduled' : 'booking.modeWalkIn')}
            </button>
          ))}
        </div>

        {isWalkIn && (
          <p className="rounded-lg bg-info-bg px-3 py-2 text-12 text-info" role="note">
            {t('booking.walkInHint')}
          </p>
        )}

        <label className="flex flex-col gap-1 text-13 text-text-2">
          <span>
            {t('booking.patientLabel')}
            <FieldLabelMark requiredMark />
          </span>
          <PatientSearchAndLink
            selected={patient}
            onSelect={(next) => {
              setPatient(next);
              if (next) setPatientRequiredError(false);
              if (!next) setRegisteredViaStepper(false);
            }}
            onRegisterNew={enterRegisterStep}
          />
          {registeredViaStepper && patient && (
            <button
              type="button"
              className="self-start text-12 font-semibold text-primary hover:underline"
              onClick={() => setFlowStep('register')}
            >
              {t('booking.quickRegister.viewRegistration', { defaultValue: 'View registration details' })}
            </button>
          )}
        </label>
        {patientRequiredError && (
          <p className="text-13 text-danger" role="alert">
            {t('booking.errors.patientRequired')}
          </p>
        )}

        {doctorPicker}

        {isWalkIn ? (
          durationField
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <DateField
                label={t('booking.dateLabel')}
                requiredMark
                value={date || null}
                min={todayDateInput()}
                max={maxBookableDateInput()}
                onChange={(value) => {
                  setDate(value);
                  setSelectedSlot('');
                  setStrictCustomTimeSelected(false);
                  setCustomTimeDialogOpen(false);
                  setPastError(false);
                  setDateRequiredError(false);
                }}
                placeholder={t('common:datePicker.chooseDate')}
                triggerAriaLabel={t('booking.dateLabel')}
                prevMonthLabel={t('common:datePicker.previousMonth')}
                nextMonthLabel={t('common:datePicker.nextMonth')}
              />
              {durationField}
            </div>
              {(allowFreeFormScheduling || strictCustomTimeSelected) && (
                <TimeField
                  label={t('booking.timeLabel')}
                  requiredMark
                  value={time || null}
                  timeFormat={timeFormat}
                  minTime={minSelectableTimeInput(date)}
                  onChange={(value) => {
                    setTime(value);
                    if (allowFreeFormScheduling) {
                      setSelectedSlot('');
                    } else {
                      const approvedStartTime = findServerApprovedStartTime(
                        date,
                        value ?? '',
                        (bookingOptions.data?.slots ?? []).map((slot) => slot.startTime),
                      );
                      setSelectedSlot(approvedStartTime ?? '');
                      if (value && !approvedStartTime) {
                        showToast({
                          type: 'error',
                          message: t('booking.availableSlots.customTimeUnavailable'),
                        });
                      }
                    }
                    setPastError(false);
                  }}
                  placeholder={t('common:datePicker.chooseTime')}
                  triggerAriaLabel={t('booking.timeLabel')}
                  dialogOpen={customTimeDialogOpen}
                  onDialogOpenChange={setCustomTimeDialogOpen}
                  hideTrigger={!allowFreeFormScheduling}
                />
              )}
            {dateRequiredError && (
              <p className="text-13 text-danger" role="alert">
                {t('booking.errors.dateRequired')}
              </p>
            )}
            {allowFreeFormScheduling && pastError && (
              <p className="text-13 text-danger" role="alert">
                {t('booking.errors.pastStartTime')}
              </p>
            )}
            {allowFreeFormScheduling && isOverloaded && (
              <p className="rounded-lg bg-warning-bg px-3 py-2 text-12 text-warning" role="alert">
                {t('booking.overload.warningBanner', {
                  defaultValue: 'Provider overload — {{count}} of {{threshold}} appointments already in this 30-min slot.',
                  count: bracketLoad.data?.count ?? 0,
                  threshold: bracketLoad.data?.threshold ?? 0,
                })}
              </p>
            )}
            {allowFreeFormScheduling && providerId && (
              <AvailableSlotPicker
                providerSelected
                durationSelected={durationMinutes > 0}
                slots={bookingOptions.data?.slots ?? []}
                timezone={bookingOptions.data?.timezone}
                selectedStartTime={selectedSlot}
                isLoading={bookingOptions.isLoading || bookingOptions.isFetching}
                isError={bookingOptions.isError}
                unavailableReason={bookingOptions.data?.unavailableReason}
                allowCustomTime
                customTimeSelected={!selectedSlot}
                warnOnCustomTime
                onSelect={(startTime) => {
                  setSelectedSlot(startTime);
                  setCustomTimeDialogOpen(false);
                }}
                onSelectCustom={() => {
                  setSelectedSlot('');
                  setCustomTimeDialogOpen(true);
                }}
                onRetry={() => void bookingOptions.refetch()}
              />
            )}
            {!allowFreeFormScheduling && (
              <AvailableSlotPicker
                providerSelected={Boolean(providerId)}
                durationSelected={durationMinutes > 0}
                slots={bookingOptions.data?.slots ?? []}
                timezone={bookingOptions.data?.timezone}
                selectedStartTime={selectedSlot}
                isLoading={bookingOptions.isLoading || bookingOptions.isFetching}
                isError={bookingOptions.isError}
                unavailableReason={bookingOptions.data?.unavailableReason}
                allowCustomTime={(bookingOptions.data?.slots.length ?? 0) > 0}
                customTimeSelected={strictCustomTimeSelected}
                onSelect={(startTime) => {
                  setSelectedSlot(startTime);
                  setStrictCustomTimeSelected(false);
                  setCustomTimeDialogOpen(false);
                }}
                onSelectCustom={() => {
                  setStrictCustomTimeSelected(true);
                  setCustomTimeDialogOpen(true);
                  setSelectedSlot('');
                }}
                onRetry={() => void bookingOptions.refetch()}
              />
            )}
          </>
        )}

        <Textarea
          label={t('booking.reasonLabel')}
          optional
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
        />

        {!isWalkIn && (
          <Textarea
            label={t('booking.internalNoteLabel')}
            optional
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
            rows={2}
          />
        )}

      </form>
    </Drawer>
  );
}
